import { useState, useEffect } from 'react'
import Button from 'react-bootstrap/Button'
import Modal from 'react-bootstrap/Modal'
import Form from 'react-bootstrap/Form'
import { useUpdateFolder } from 'api/hooks/use-folders'
import { useQueryClient } from 'react-query'
import { toast } from 'react-toastify'
import { MAX_FOLDER_DEPTH, buildFolderTree, eligibleParents, subtreeHeight } from 'dashboard/folders/folder-tree'

// The keyboard path for what dragging a folder card onto another one does. Only folders that can
// actually take this one are offered: not itself, nothing nested inside it, and nothing so deep that
// the branch would end up past MAX_FOLDER_DEPTH.
const MoveFolderModal = ({ folder, folders = [], show, onHide }) => {
  const [parentId, setParentId] = useState('')
  const queryClient = useQueryClient()

  useEffect(() => {
    if (folder) setParentId(folder.parent_id || '')
  }, [folder])

  const { mutate: updateFolder, isLoading } = useUpdateFolder({
    options: {
      onSuccess: () => {
        onHide()
        queryClient.invalidateQueries('folders')
      },
      onError: () => {
        toast.error('Failed to move folder')
      }
    }
  })

  const handleSubmit = (e) => {
    e.preventDefault()
    if (folder?.id) updateFolder({ id: folder.id, parent_id: parentId || null })
  }

  const tree = buildFolderTree(folders)
  const options = folder ? eligibleParents(tree, folder, folders) : []
  const branchHeight = folder ? subtreeHeight(tree, folder) : 0

  return (
    <Modal show={show} onHide={onHide}>
      <Form onSubmit={handleSubmit}>
        <Modal.Header closeButton>
          <Modal.Title>Move {folder?.name}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>Move into</Form.Label>
            <Form.Select value={parentId} onChange={(e) => setParentId(e.target.value)}>
              <option value="">Top level</option>
              {options.map((option) => (
                <option key={option.id} value={option.id}>{option.path}</option>
              ))}
            </Form.Select>
            <Form.Text className="text-muted">
              Folders nest up to {MAX_FOLDER_DEPTH} levels deep.
              {branchHeight > 0 && ` This one has ${branchHeight} ${branchHeight === 1 ? 'level' : 'levels'} below it, so only folders with room for them are listed.`}
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="white" onClick={onHide}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={isLoading || parentId === (folder?.parent_id || '')}>
            Move
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  )
}

export default MoveFolderModal
